import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Steps } from 'antd';
import { API } from '@services/api.service';
import AlertBox from '@/components/alert-box';
import { OPERATION } from '@/constants';
import useQueryParamsContext from '@/contexts/query-params/useQueryParamsContext';
import { useNotify } from '@/hooks';
import { cn } from '@/libs/utils';
import { QUERY_KEY } from '@/shared/constants';
import { isEmployeeRequestFormDisabled } from '@/utils/formUtils';
import TravelCalculationStep from './TravelCalculationStep';
import TravelInvoicesStep from './TravelInvoicesStep';
import TravelOrderStep from './TravelOrderStep';
import TravelSummaryStep from './TravelSummaryStep';

const STEP_ORDER = 0;
const STEP_CALCULATION = 1;

/**
 * Official travel (putni nalog) wizard.
 *
 * The travel order is filled in over four steps and each step saves on its own,
 * because the trip is described before it happens and settled after it. Step one
 * creates the request; every later step edits it, and the server recomputes the
 * settlement on each save — the wizard never calculates money locally.
 */
export default function OfficialTravelForm({
  id,
  formData,
  typeOfLeave,
  employeeId,
  handleRefetch,
  hasQueryParams = true,
  reasonRejectionMessage,
  stateValue = {},
  isEmployee = false,
}) {
  const { t } = useTranslation(['officialTravel', 'messages', 'forms']);
  const { queryParams } = useQueryParamsContext();
  const queryClient = useQueryClient();
  const notification = useNotify();

  const [currentStep, setCurrentStep] = useState(STEP_ORDER);

  const source = hasQueryParams ? queryParams : stateValue;
  const { action, requestId } = source;

  // The id in the query survives a switch to the "new request" tab, so it only
  // counts as the request being worked on while the wizard was actually opened
  // to view or edit one. Without this, opening an approved travel order and
  // then starting a new request reopened the approved one — editable.
  const openedRequestId = [OPERATION.view, OPERATION.edit].includes(action)
    ? requestId
    : null;

  // Step one creates the request, so from then on the wizard works on either
  // the request it was opened with or the one it just created.
  const [createdRequestId, setCreatedRequestId] = useState(null);
  const activeRequestId = openedRequestId || createdRequestId;

  // Pointing the wizard at something else — another request, or the new-request
  // tab — must not leave it holding on to what it created before.
  useEffect(() => {
    setCreatedRequestId(null);
  }, [requestId, action]);

  // The travel order is read back on every step — including a step already
  // filled in and revisited — so the fetch follows the request itself rather
  // than the view/edit action the wizard happened to be opened with.
  const isEnabled = Boolean(activeRequestId && employeeId);

  const params = useMemo(
    () => ({
      query: QUERY_KEY.request,
      employeeId,
      action,
      requestId: activeRequestId,
    }),
    [employeeId, action, activeRequestId]
  );

  const { data: requestData } = useQuery({
    queryKey: [
      params.query,
      {
        employeeId: params.employeeId,
        action: params.action,
        requestId: params.requestId,
      },
    ],
    queryFn: async () => await API.requests.getRequestById(activeRequestId),
    enabled: isEnabled,
    retry: false,
  });

  // The shared guard compares `status` to the plain string, but the API returns
  // it as a translated enum object, so an approved request slips through as
  // editable. An approved travel order is already attached to the salary, so it
  // is closed here regardless of how the wizard was opened.
  const requestStatus = requestData?.status?.key || requestData?.status;
  const disabled =
    isEmployeeRequestFormDisabled(action ?? '', requestData) ||
    requestStatus === 'ACCEPTED';

  // A step save is not the end of the work: the person keeps going to the next
  // step, so the lists behind the wizard are refreshed in place. The host's
  // handleRefetch closes its dialog, which is only right once a decision has
  // been made on the order.
  const refetchRequest = useCallback(async () => {
    await Promise.all(
      [
        params.query,
        QUERY_KEY.allDocuments,
        QUERY_KEY.employeeHrDocuments,
        // The home page lists have no shared key constant.
        'dashboard',
      ].map((key) => queryClient.invalidateQueries({ queryKey: [key] }))
    );
  }, [queryClient, params.query]);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (payload) => {
      if (activeRequestId) {
        return await API.requests.updateRequestById(activeRequestId, payload);
      }

      return await API.requests.createNewRequest(payload);
    },
    onSuccess: async (response) => {
      // A create returns the new request; remember it so the remaining steps
      // edit instead of creating a second travel order.
      const newId = response?.item?._id || response?.item?.requestId;
      if (!activeRequestId && newId) setCreatedRequestId(newId);

      notification.success({
        message: t('success', { ns: 'messages' }),
        description: response?.message,
      });

      await refetchRequest();
    },
    onError: (error) => {
      notification.error({
        message: t('error', { ns: 'messages' }),
        description: error?.response?.data?.error || error?.message,
      });
    },
  });

  const basePayload = useMemo(
    () => ({
      typeOfLeave,
      employee: formData?._id || employeeId,
      company: formData?.company?._id || formData?.company,
      firstName: formData?.firstName,
      lastName: formData?.lastName,
    }),
    [typeOfLeave, formData, employeeId]
  );

  const handleOrderSubmit = useCallback(
    async (travelOrder) => {
      await mutateAsync({ ...basePayload, travelOrder });
      setCurrentStep(STEP_CALCULATION);
    },
    [mutateAsync, basePayload]
  );

  const handleCalculationSubmit = useCallback(
    async (travelOrderCalculation) => {
      if (!activeRequestId) {
        notification.warning({
          message: t('validation.orderFirst', { ns: 'officialTravel' }),
        });
        return;
      }

      await mutateAsync({ ...basePayload, travelOrderCalculation });
    },
    [mutateAsync, basePayload, activeRequestId, notification, t]
  );

  const handleInvoicesSubmit = useCallback(
    async (travelInvoices) => {
      if (!activeRequestId) {
        notification.warning({
          message: t('validation.orderFirst', { ns: 'officialTravel' }),
        });
        return;
      }

      await mutateAsync({ ...basePayload, travelInvoices });
    },
    [mutateAsync, basePayload, activeRequestId, notification, t]
  );

  const handleAttachmentsChange = useCallback(
    async (travelAttachments) => {
      if (!activeRequestId) {
        notification.warning({
          message: t('validation.orderFirst', { ns: 'officialTravel' }),
        });
        return;
      }

      await mutateAsync({ ...basePayload, travelAttachments });
    },
    [mutateAsync, basePayload, activeRequestId, notification, t]
  );

  const handleRatesSubmit = useCallback(
    async (travelExchangeRates) => {
      if (!activeRequestId) return;

      await mutateAsync({ ...basePayload, travelExchangeRates });
    },
    [mutateAsync, basePayload, activeRequestId]
  );

  // Approving is a decision, not an edit, so it goes through the platform's
  // status transition — the same call the HR requests list makes.
  const handleApprove = useCallback(async () => {
    if (!activeRequestId) return;

    await mutateAsync({ status: 'ACCEPTED' });
    // The decision is made, so the host may close its dialog now.
    handleRefetch?.();
  }, [mutateAsync, activeRequestId, handleRefetch]);

  const { mutateAsync: submitForApproval, isPending: isSubmitting } =
    useMutation({
      mutationFn: async () =>
        await API.requests.submitOfficialTravel(activeRequestId),
      onSuccess: async () => {
        notification.success({
          message: t('success', { ns: 'messages' }),
          description: t('summary.submitSuccess', { ns: 'officialTravel' }),
        });
        await refetchRequest();
      },
      onError: (error) => {
        notification.error({
          message: t('error', { ns: 'messages' }),
          description: error?.response?.data?.error || error?.message,
        });
      },
    });

  const travelOrder = requestData?.travelOrder;
  const travelOrderCalculation = requestData?.travelOrderCalculation;
  const travelInvoices = requestData?.travelInvoices || [];
  const travelAttachments = requestData?.travelAttachments || [];

  const steps = [
    {
      title: t('steps.order', { ns: 'officialTravel' }),
      content: (
        <TravelOrderStep
          travelOrder={travelOrder}
          disabled={disabled}
          isEmployee={isEmployee}
          isPending={isPending}
          onSubmit={handleOrderSubmit}
        />
      ),
    },
    {
      title: t('steps.calculation', { ns: 'officialTravel' }),
      content: (
        <TravelCalculationStep
          travelOrder={travelOrder}
          travelOrderCalculation={travelOrderCalculation}
          disabled={disabled}
          isPending={isPending}
          onSubmit={handleCalculationSubmit}
        />
      ),
    },
    {
      title: t('steps.invoices', { ns: 'officialTravel' }),
      content: (
        <TravelInvoicesStep
          requestId={activeRequestId}
          travelOrder={travelOrder}
          travelInvoices={travelInvoices}
          travelAttachments={travelAttachments}
          employeeId={formData?._id || employeeId}
          disabled={disabled}
          isPending={isPending}
          onSubmit={handleInvoicesSubmit}
          onAttachmentsChange={handleAttachmentsChange}
        />
      ),
    },
    {
      title: t('steps.summary', { ns: 'officialTravel' }),
      content: (
        <TravelSummaryStep
          travelOrder={travelOrder}
          travelOrderCalculation={travelOrderCalculation}
          travelExchangeRates={requestData?.travelExchangeRates || []}
          // The rates are the employer's call, like the per diem itself.
          ratesDisabled={disabled || isEmployee}
          isSavingRates={isPending}
          onRatesSubmit={handleRatesSubmit}
          requestId={activeRequestId}
          canBook={!isEmployee}
          status={requestData?.status}
          canApprove={Boolean(activeRequestId) && !isEmployee}
          isApproving={isPending}
          onApprove={handleApprove}
          travelSubmission={requestData?.travelSubmission}
          canSubmit={Boolean(activeRequestId) && isEmployee && !disabled}
          isSubmitting={isSubmitting}
          onSubmitForApproval={() => submitForApproval().catch(() => {})}
        />
      ),
    },
  ];

  return (
    <div
      className={cn('grid gap-4')}
      id={`employee_request-officialTravel-form_${employeeId}_${id}`}
    >
      {reasonRejectionMessage && (
        <AlertBox variant="warning" className="mb-0 p-2 text-left">
          <div className="grid">
            <h4 className="font-semibold">
              <span className="text-black">
                {t('reasonOfRejection', { ns: 'forms' })}:{' '}
              </span>
              <span className="w-max">{reasonRejectionMessage}</span>
            </h4>
          </div>
        </AlertBox>
      )}

      <Steps
        current={currentStep}
        onChange={setCurrentStep}
        size="small"
        items={steps.map(({ title }, index) => ({
          title,
          // Later steps edit an existing travel order, so they stay closed
          // until step one has produced one.
          disabled: index > STEP_ORDER && !activeRequestId,
        }))}
      />

      {!activeRequestId && currentStep !== STEP_ORDER && (
        <AlertBox variant="warning" className="mb-0">
          <p>{t('validation.orderFirst', { ns: 'officialTravel' })}</p>
        </AlertBox>
      )}

      <div>{steps[currentStep].content}</div>
    </div>
  );
}
